import axios from "axios";
import { toast } from "react-toastify";
import React from "react";
import { HOST_API } from "../config";

// ----------------------------------------------------------------------

const axiosInstance = axios.create({
  baseURL: HOST_API,
  // Auth is Bearer-token (localStorage), NOT cookies. Sending credentials to the
  // cross-subdomain API (portal.grapetask.co) made Edge/Safari "Tracking
  // Prevention" block the request → users saw CORS/Network errors on login.
  withCredentials: false,
});

// In Explorer Mode the user is previewing a role their account does NOT have
// (localStorage.Role !== RealRole), so role-changing actions are blocked. Instead
// of a generic "Actions are disabled", explain WHY and guide them to the account
// they need to create for the role they're currently exploring.
function explorerBlockMessage(exploredRole) {
  const r = (exploredRole || "").toLowerCase().trim();
  if (r.includes("client")) {
    return "Explorer Mode: you're previewing the Client experience. Please create a Client account to post a job — tap here to sign up.";
  }
  if (
    r.includes("bidder") ||
    r.includes("representative") ||
    r.includes("middleman") ||
    r === "bd" ||
    (r.includes("business") && r.includes("developer"))
  ) {
    return "Explorer Mode: you're previewing the Business Developer experience. Please create a Business Developer account to proceed — tap here to sign up.";
  }
  if (r.includes("expert") || r.includes("freelancer")) {
    return "Explorer Mode: you're previewing the Expert experience. Please create an Expert account to proceed — tap here to sign up.";
  }
  return "Explorer Mode: this preview is read-only. Tap here to create an account for this role.";
}

// Short key for the explored role, used to pre-select it on the signup form
// (/signup?role=bd). Mirrors normalizeRole() in Navbar.js.
function explorerRoleKey(exploredRole) {
  const r = (exploredRole || "").toLowerCase().trim();
  if (r.includes("client")) return "client";
  if (
    r.includes("bidder") ||
    r.includes("representative") ||
    r.includes("middleman") ||
    r === "bd" ||
    (r.includes("business") && r.includes("developer"))
  ) {
    return "bd";
  }
  if (r.includes("expert") || r.includes("freelancer")) return "expert";
  return "";
}

function getRoleName(roleStr) {
  const r = (roleStr || "").toLowerCase().trim();
  if (r.includes("client")) return "Client";
  if (
    r.includes("bidder") ||
    r.includes("representative") ||
    r.includes("middleman") ||
    r === "bd" ||
    (r.includes("business") && r.includes("developer"))
  ) return "Business Developer";
  if (r.includes("expert") || r.includes("freelancer")) return "Expert";
  return "Visitor";
}

// The user is logged into a different role and AuthRoute blocks logged-in users
// from /signup, while each role is a separate account — so end the current
// session before opening signup with the target role pre-selected.
function goCreateRoleAccount(roleKey) {
  try {
    localStorage.clear();
  } catch (e) {
    /* ignore storage errors */
  }
  window.location.href = roleKey ? `/signup?role=${roleKey}` : "/signup";
}

// Attach Bearer token so protected APIs (e.g. job-invitations) always get auth
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken");
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Check Explorer Mode for mutations (POST, PUT, DELETE, PATCH)
    const role = localStorage.getItem("Role");
    const realRole = localStorage.getItem("RealRole");
    const method = (config.method || "").toUpperCase();

    if (token && role && realRole && role !== realRole && ["POST", "PUT", "DELETE", "PATCH"].includes(method)) {
      const url = config.url || "";
      // Allow auth/logout/validate/refresh, media uploads to work
      if (
        !url.includes("logout") &&
        !url.includes("auth/validate") &&
        !url.includes("refresh-token") &&
        !url.includes("upload-media") &&
        !url.includes("messages/file")
      ) {
        
        const realRoleName = getRoleName(realRole);
        const exploredRoleName = getRoleName(role);

        // toastId dedupes the stack when several blocked requests fire at once
        // (e.g. a page that POSTs to multiple endpoints on load). Clicking the
        // toast logs out and opens signup with the explored role pre-selected.
        const toastContent = (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ fontSize: '13.5px', color: '#b45309', fontWeight: 500, lineHeight: 1.5 }}>
              <b>Read-Only Mode:</b> You are a {realRoleName} visiting the {exploredRoleName} dashboard. Actions are restricted.
            </span>
          </div>
        );

        toast(toastContent, {
          containerId: "global",
          toastId: "explorer-mode-block",
          position: "top-right",
          closeOnClick: true,
          hideProgressBar: true,
          style: {
            background: '#fffbeb',
            color: '#b45309',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
            padding: '12px 16px',
            border: 'none',
          },
          icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
          ),
          autoClose: 3500,
        });
        return Promise.reject({
          message: "Operation cancelled in Explorer Mode.",
          isExplorerBlocked: true
        });
      }
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// On 401: clear token and redirect to login (session=expired) if on a protected page.
// Session can also "expire" from inactivity timeout (see SESSION_CONFIG in routes/index.js – now 4 hours).
const PROTECTED_PATH_PREFIXES = [
  "/dashboard",
  "/profile",
  "/orders",
  "/earning",
  "/spending",
  "/chat",
  "/payout",
  "/admin",
  "/referral",
  "/applicants",
  "/account",
  "/gigs",
  "/level",
  "/offer",
  "/hire-expert",
  "/buyer-request",
  "/started-job",
  "/multi-steps",
  "/bonus",
  "/interview",
  "/freelancers",
  "/payment-success",
  "/payout-method",
  "/referal",
  "/enter-code",
  "/thanks",
  "/referal-list",
  "/accept",
  "/progress",
  "/complete",
  "/profile-user",
  "/offer-detail",
  "/job-invitations",
  "/meet-top-rated",
  "/search-gigs",
  "/inbox",
  "/purchase-bids",
  "/user-buyer-request",
  "/profile-other",
  // Current camelCase / actual route paths (the normalize above catches the
  // hyphen↔camelCase forms; these cover the few that don't reduce identically).
  "/hireExpert",
  "/buyerRequest",
  "/userBuyerRequest",
  "/multiSteps",
  "/payoutMethod",
  "/profileOtherPerson",
  "/buy-bids",
];
// Normalize so camelCase routes (e.g. /hireExpert) match hyphenated prefixes
// (e.g. /hire-expert): lowercase + strip hyphens. Without this, a 401 on
// /hireExpert / /buyerRequest / /multiSteps / /payoutMethod / /profileOtherPerson
// was NOT recognized as a protected page, so instead of cleanly redirecting to
// login on session-expiry the app showed a raw "Request failed with status code 401".
const normPath = (s) => (s || "").toLowerCase().replace(/-/g, "");
function isProtectedPath(pathname) {
  const p = normPath((pathname || "").split("?")[0]);
  if (p === "/" || p === "") return false;
  if (p === normPath("/auth/callback")) return false;
  return PROTECTED_PATH_PREFIXES.some((prefix) => {
    const np = normPath(prefix);
    return p === np || p.startsWith(np + "/");
  });
}

let didRedirect401 = false;
let validating401 = false;

function doLogout() {
  if (didRedirect401) return;
  didRedirect401 = true;
  localStorage.removeItem("accessToken");
  localStorage.removeItem("UserData");
  localStorage.removeItem("Role");
  localStorage.removeItem("RealRole");
  window.location.replace("/login?session=expired");
}

async function clearAuthAndRedirect(error) {
  if (didRedirect401 || validating401) return;
  const pathname =
    typeof window !== "undefined" ? window.location.pathname : "";
  if (pathname === "/auth/callback") return;

  // If user is in explorer mode, check if the error is from the token validation API.
  // Bypass logging out on other 401s (since it is likely due to role middleware mismatch).
  const role = localStorage.getItem("Role");
  const realRole = localStorage.getItem("RealRole");
  const isExplorer = role && realRole && role !== realRole;

  if (isExplorer) {
    const failedUrl = error?.config?.url || "";
    if (!failedUrl.includes("auth/validate")) {
      console.warn("401 ignored in Explorer Mode for:", failedUrl);
      return;
    }
  }

  // Only consider tearing down the session on a protected page. A stray 401 on a
  // public/transition page must NOT wipe a valid token.
  if (!(pathname && isProtectedPath(pathname))) return;

  const failedUrl = error?.config?.url || "";
  const token = localStorage.getItem("accessToken");
  if (!token) return; // nothing to tear down

  // DON'T log out on a single, possibly transient/endpoint-specific 401. First
  // CONFIRM the token is actually dead via /auth/validate; only log out if that
  // also 401s. If /auth/validate itself was the failed request, the token is
  // already confirmed dead. Fail-open (never log out) on a network error.
  if (failedUrl.includes("auth/validate")) {
    doLogout();
    return;
  }
  validating401 = true;
  try {
    const res = await fetch(HOST_API + "auth/validate", {
      headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
    });
    if (res.status === 401) doLogout();
    // else: token still valid → the 401 was transient/endpoint-specific → ignore.
  } catch (_e) {
    // network error → do NOT log out
  } finally {
    validating401 = false;
  }
}

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if (status === 401) {
      clearAuthAndRedirect(error);
    }
    return Promise.reject(error);
  },
);

export default axiosInstance;
